import { RefObject, useEffect, useRef } from "react";
import {
  applyDomTextHighlight,
  clearDomTextHighlight,
  DomTextHighlightOptions,
  PROFILE_SEARCH_HIGHLIGHT_CLASS,
} from "../utils/domTextHighlight";

type UseDomTextHighlightOptions = DomTextHighlightOptions & {
  enabled?: boolean;
  /** Re-run highlighting when async profile content finishes loading. */
  contentVersion?: unknown;
};

/**
 * Highlights every visible text substring inside `containerRef` using `<mark>`.
 * Re-applies when the container subtree changes (e.g. profile sections loading async).
 */
export function useDomTextHighlight(
  containerRef: RefObject<HTMLElement | null>,
  searchTerm: string | null | undefined,
  options: UseDomTextHighlightOptions = {},
): void {
  const {
    enabled = true,
    className = PROFILE_SEARCH_HIGHLIGHT_CLASS,
    contentVersion,
  } = options;

  const isApplyingRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const term = String(searchTerm ?? "").trim();
    if (!container || !enabled || !term) {
      if (container) {
        clearDomTextHighlight(container, className);
      }
      return undefined;
    }

    let cancelled = false;
    let debounceId: number | undefined;

    const runHighlight = () => {
      if (cancelled || !containerRef.current) {
        return;
      }
      isApplyingRef.current = true;
      try {
        applyDomTextHighlight(containerRef.current, term, { className });
      } finally {
        isApplyingRef.current = false;
      }
    };

    const scheduleHighlight = () => {
      if (debounceId != null) {
        window.clearTimeout(debounceId);
      }
      debounceId = window.setTimeout(() => {
        debounceId = undefined;
        runHighlight();
      }, 80);
    };

    scheduleHighlight();

    const observer = new MutationObserver(() => {
      if (isApplyingRef.current) {
        return;
      }
      scheduleHighlight();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      cancelled = true;
      if (debounceId != null) {
        window.clearTimeout(debounceId);
      }
      observer.disconnect();
      clearDomTextHighlight(container, className);
    };
  }, [containerRef, searchTerm, enabled, className, contentVersion]);
}
